import { INBOX_DEFAULT_WINDOW_DAYS, readableDate } from '@pams/shared';
import { ARRANGEMENT_SEARCH_READS } from '@/lib/arrangementSearch';

/**
 * ⚠️ [amended C3c.2] THIRTY-SIX CHARACTERS, WITH AN ELLIPSIS — on the header AND on a duty row.
 *
 * ⚠️ IT WAS THIRTY, AND THE COLUMN OUTGREW IT. [C3c.2] took the Arrangement column from 26rem to
 * 34rem so the header row would sit on one line; at thirty characters a procession title was
 * still being cut well inside the space the column now has, which is a truncation the layout no
 * longer needs. Raised on the client's own instruction.
 *
 * ⚠️ THE CSS `truncate` IS STILL THE BACKSTOP, AND THIS IS STILL THE BINDING CUT. The tree cell
 * clips whatever overflows its box, but it cannot restore what this has already removed — so
 * this number must stay BELOW what the column can show, or the ellipsis an operator sees is
 * ours rather than the browser's and the title is shortened twice.
 *
 * ⚠️ THE RECOVERY PATH IS WHY THE `title` IS NOT OPTIONAL. Before §2 the full name was reachable
 * because the name WAS a link carrying a `title`; §2c makes the header text rather than a link,
 * so hover is the only way left to read a truncated one.
 */
export const INBOX_NAME_MAX = 36;

export const truncateName = (value: string): string =>
  value.length > INBOX_NAME_MAX ? `${value.slice(0, INBOX_NAME_MAX - 1).trimEnd()}…` : value;

export const INBOX_COPY = {
  /**
   * ⚠️ THE ARRANGEMENT'S DUTY SPAN **WITHIN THE WINDOW**, never its whole life. One date when
   * the group has one night, which is the common case and must not read as a range of one.
   */
  groupSpan: (from: string, to: string): string =>
    from === to ? (readableDate(from) ?? from) : `${readableDate(from) ?? from} – ${readableDate(to) ?? to}`,
  /**
   * ⚠️ [D4.5 §4] "Upcoming PA" → "Assign PA". The screen is where a nodal DOES the naming,
   * not merely where they see what is coming — and the sidebar, the dashboard tile and the
   * fill screen's breadcrumb all say the same thing. Route paths and identifiers are
   * untouched: `/inbox`, `InboxPage`, `queryKeys.inbox`.
   */
  title: 'Assign PA',
  /*
   * ⚠️ `subtitle` AND `defaultWindowHint` WERE HERE AND ARE GONE with the header's subtitle.
   * §9: a string with no reader is dead code. Neither claim was lost — the count is in the
   * table's pager, and `toPlaceholder` below still states the default window from the SAME
   * shared constant the server applies, which is what stopped the sentence and the query
   * drifting apart in the first place.
   */
  emptyTitle: 'Nothing to fill',
  emptyBody: 'When the CP office publishes an order that asks your division for people, it appears here.',
  /**
   * ⚠️ [C5c §7a] AN EMPTY **SEARCHED** PAGE IS NOT "NOTHING TO FILL". That sentence tells a nodal their
   * division has no work; an empty search says only that no order in the window matched. The body
   * is the one sentence (`lib/arrangementSearch.ts`) plus the window, because the inbox's dates bound
   * the search — an order whose duties fall outside them is not an answer here.
   *
   * ⚠️ THE TERM IS NOT QUOTED. [C5b §6]'s stale-term hazard: `keepPreviousData` holds the old page
   * on screen while the next term loads, so a quoted term can name the wrong search.
   */
  noMatchTitle: 'Nothing matches this search',
  noMatchBody: `Search looks at each order’s ${ARRANGEMENT_SEARCH_READS}, within the dates shown.`,
  searchPlaceholder: ARRANGEMENT_SEARCH_READS,
  /*
   * ⚠️ [amended C5b review] `named` WAS HERE — `8 of 12`, "not `8 / 12`, because it is read aloud in a
   * briefing" ([D4.5 §3d]). The Progress column now reads `namedOf`, the one spelling every surface
   * stating a fill count uses (`8 of 12 named` still reads aloud), and a string with no reader is dead.
   */

  /**
   * ⚠️ [amended C3c.1] THE `$topN` CAP, SAID OUT LOUD — AND §2 SHIPPED IT SILENT.
   *
   * `INBOX_MAX_DUTY_ROWS` bounds each arrangement's duty rows at 40 so an intermediate document
   * cannot approach the 16 MB cap, and CLAUDE.md §7 [C3c §2] records the mitigation in terms:
   * "THE CAP IS STATED, NEVER SILENT. `dutyRowCount` carries the true number so the header can
   * read '40 of 192'". Nothing read `dutyRowCount`, so a nodal expanding a busy order saw 40
   * rows and the rest of their work was absent with no count, no dash and no sentence.
   *
   * ⚠️ IT IS ON THE HEADER, WHERE THE TRUE COUNT IS. Putting it on the last child row would put
   * a claim about a group inside a row that is one duty — and the two grains are the whole of §2.
   */
  dutyRowsCapped: (shown: number, total: number): string => `${String(shown)} of ${String(total)} duties shown`,

  /**
   * ⚠️ [P7A §4] WHY A DUTY ROW CLOSED SHORT — the division's own words, under its `Closed short` dot,
   * so a nodal reading `0 of 12 named · Closed short` is not left to ring somebody to ask. Several
   * reasons when a division and one of its units closed separately; the row is division × duty.
   * It never names who declared ([P7A §4]: `completeShares` may have closed it from above).
   */
  closedShortReason: (reasons: readonly string[]): string => `Reason: ${reasons.join(' · ')}`,

  /** ⚠️ Why a header cell is blank. See `fillStateFiltered` in the page. */
  filteredHeader: 'Not shown while a status filter is applied — it would describe the filtered rows only',

  dash: '—',
  /**
   * ⚠️ [D4.5 §3d] A SORTABLE COLUMN'S `key` IS ITS `sortBy` VALUE, AND THAT IS NOT COSMETIC.
   *
   * `useServerTable` hands AntD's `columnKey` straight to `sortFields.includes(...)`, so a
   * key outside `INBOX_SORT_FIELDS` silently falls through to the DEFAULT sort. `key: 'time'`
   * was exactly that: the Time header had a sorter, and clicking it re-sorted by fill state
   * in the clicked direction. Nothing failed, nothing warned, and the column had never once
   * sorted by time.
   */
  columns: {
    arrangement: 'Arrangement',
    /** Ordinal AND place: the ordinal alone is meaningless, the place alone does not disambiguate. */
    /**
     * ⚠️ [D4.4 §1c] "Asked of" RATHER THAN "Division", because the cell says BOTH — the
     * division and every unit addressed within it. A column headed `Division` above a cell
     * reading `CEN · BNP, Any` names the wrong half of what is in it.
     *
     * ⚠️ [D4.5 §3d] §3d ASKED FOR "Division" AND THIS DEPARTS FROM IT DELIBERATELY, confirmed
     * before building. The new cell holds MORE of both halves than the one that produced the
     * original argument, so the argument holds harder rather than less.
     */
    addressee: 'Asked of',
    date: 'Date',
    time: 'Time',
    progress: 'Named',
    status: 'Status',
  },
  /** `DP1 · Esplanade`, collapsing when the card has no name of its own and the two agree. */
  filters: {
    from: 'From',
    to: 'To',
    status: 'Status',
    division: 'Division',
    policy: 'Policy',
    show: 'Show',
    search: 'Search',
  },
  /*
   * ⚠️ [C1d §2] THE SAME THREE WORDS AS EVERY OTHER LIST ON THE ARCHIVE AXIS. The brief said
   * "Active | Archived"; the app has said Current | Archived | All on five screens since
   * [LIFECYCLE], and a two-member vocabulary here would be a second answer to a question one
   * `ShowLifecycle` already answers — the failure mode being that the forgotten one is silent.
   */
  showCurrent: 'Current',
  showArchived: 'Archived',
  showAll: 'All',
  fromPlaceholder: 'Today',
  toPlaceholder: `In ${String(INBOX_DEFAULT_WINDOW_DAYS)} days`,
  anyStatus: 'Any status',
  anyDivision: 'Any division',
  anyPolicy: 'Any policy',
} as const;
